<template>
  <b-container>
    <b-row class="headline" align-v="center">
      <b-col v-if="live">
        <h4>{{ live.title }}</h4>
        <small>
          <font-awesome-icon icon="calendar-alt"></font-awesome-icon> &nbsp; {{ when }}
          <span v-if="live.is_live">
            &nbsp; &mdash; &nbsp;
            <font-awesome-icon icon="eye" /> {{ viewers.length }} watching live
          </span>
          <span v-else-if="live.is_cancelled" class="cancelled">
            &nbsp; &mdash; &nbsp;
            cancelled
          </span>
          <span v-else-if="live.has_ended">
            &nbsp; &mdash; &nbsp;
            ended
          </span>
        </small>
      </b-col>
      <b-col cols="6" class="text-right">
        <b-button v-if="live && live.is_live" variant="secondary" size="sm" v-b-modal.stopModal>
          End live stream
        </b-button>
        <b-button v-if="live && !live.is_live && !live.is_cancelled" variant="secondary" size="sm" @click="start">
          Start live stream
        </b-button>
        <b-button v-if="live" variant="secondary" size="sm" :to="{name: 'artist-live-stream-edit', params: {id: live.id}}">
          Edit
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="player-col">
        <Player v-if="live"
                :live="live"
                :pageUrl="$route.fullPath"
                :canBan="true"
                :autoplay="true"
                @members="viewers = $event"
                @ban="banUser"
                @online="isOnline = $event">
        </Player>
      </b-col>
    </b-row>

    <b-row v-if="live && live.chat_type != 'disabled' && !isOnline" class="my-2">
      <b-col>
        <CookieInfo>
          If you have trouble playing the stream, we recommend using Chrome as your browser.
          Please also ensure that your browser allows 3rd-Party cookies.
        </CookieInfo>
      </b-col>
    </b-row>

    <b-row class="mt-4" v-if="streamConfig">
      <b-col>
        <h4>Encoder information</h4>
      </b-col>
    </b-row>

    <b-row class="encoder-info" v-if="streamConfig">
      <b-col>
        <p>
          Use this information to set up your video encoder (<router-link :to="{ name: 'site-streaming-guide' }" target="_blank">detailed guide</router-link>):
        </p>
        <b-form-group label="Publish URL" horizontal>
          <b-form-input type="text" readonly :value="streamConfig.publishing_point_primary" />
        </b-form-group>
        <b-form-group label="Stream Name (Key)" horizontal>
          <b-form-input type="text" readonly :value="streamConfig.stream_name" />
        </b-form-group>
        <b-form-group label="Username" horizontal>
          <b-form-input type="text" readonly :value="streamConfig.login" />
        </b-form-group>
        <b-form-group label="Password" horizontal>
          <b-form-input type="text" readonly :value="streamConfig.password" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-modal id="stopModal"
             ref="stopModal"
             title="End Live Stream"
             @ok="stop"
             centered>
      <div class="content">
        This live stream cannot be restarted. Are you sure you want to end this live stream?
      </div>
    </b-modal>

    <b-modal id="stoppedMessage"
             ref="stoppedMessage"
             title="Stop Live Stream"
             :ok-only="true"
             centered>
      <div class="content">
        <p>
          Live stream stopped.
        </p>
        <p>
          Make sure you also stop your video encoder.
        </p>
      </div>
    </b-modal>

    <b-modal id="banModal"
             ref="banModal"
             title="Block user"
             @ok="confirmBanUser"
             centered>
      <div class="content" v-if="userToBan">
        Block {{ displayName(userToBan) }}?
      </div>
    </b-modal>

  </b-container>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';

  .container {
    margin-top: 2em;
    max-width: unset;
  }

  .headline {
    margin-bottom: 1em;

    .cancelled {
      text-color: $tomato;
    }
  }

  .encoder-info {
    background-color: #1e1f36;
    border-radius: 4px;
    padding: 1em;
  }

  /deep/ .modal-header .close {
    color: $white;
  }
</style>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from '@/utils/moment'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import Player from '@/components/widgets/Player'
import CookieInfo from '@/components/widgets/CookieInfo'

import { profileHelpers } from '@/store/helpers'

export default {
  name: 'ManageLiveStream',
  components: {
    FontAwesomeIcon,
    Player,
    CookieInfo
  },
  data () {
    return {
      live: null,
      userToBan: null,
      isOnline: false,
      viewers: []
    }
  },
  computed: {
    when () {
      if (!this.live) return ''
      return moment(this.live.start).format('MMM Do [at] h:mm a')
    },
    streamConfig () {
      if (!this.live || !this.live.dacast_channel_details.config) return null
      return this.live.dacast_channel_details.config
    },
    ...mapGetters('profile', ['currentUser'])
  },
  watch: {
    '$route': 'initPage'
  },
  created () {
    this.initPage()
  },
  methods: {
    async initPage () {
      let artist = this.$store.getters['artist/artist']
      let live = await this.$store.dispatch('live/getOwnLiveEvent', {id: this.$route.params.id})
      if (live.artist.id !== artist.id) {
        this.$router.push({name: 'artist-dashboard'})
        return
      }

      this.live = live
    },
    async stop () {
      this.showModal('Stopping live stream ...')
      try {
        await this.$store.dispatch('live/stopLiveEvent', {id: this.$route.params.id})
      } catch (e) {
        this.$store.dispatch('flash/showMessage', {message: 'Error stopping live stream', type: 'error'})
        this.hideModal()
        return
      }
      this.live = await this.$store.dispatch('live/getOwnLiveEvent', {id: this.$route.params.id})
      this.hideModal()
      this.$refs.stoppedMessage.show()
    },
    async start () {
      this.showModal('Starting live stream ...')
      try {
        await this.$store.dispatch('live/startLiveEvent', {id: this.$route.params.id})
      } catch (e) {
        this.$store.dispatch('flash/showMessage', {message: 'Error starting live stream', type: 'error'})
        this.hideModal()
        return
      }
      this.live = await this.$store.dispatch('live/getOwnLiveEvent', {id: this.$route.params.id})
      this.hideModal()
      this.$store.dispatch('flash/showMessage', {message: 'Live stream started'})
    },
    banUser (user) {
      this.userToBan = user
      this.$refs.banModal.show()
    },
    confirmBanUser () {
      if (!this.live || !this.userToBan) return
      const user = this.userToBan
      this.userToBan = null
      this.$store.dispatch('live/blockUser', {eventId: this.live.id, profileId: user.id})
    },
    ...profileHelpers,
    ...mapActions('flash', ['showModal', 'hideModal'])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/artist/ManageLiveStream.vue